import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Search,
  Mail,
  Calendar,
  User,
  Edit,
  Trash2,
  Eye,
  Play,
  StopCircle,
  Loader2,
  FileText,
  MoreHorizontal,
  MoreVertical,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu"; // shadcn dropdown

import {
  getActivity,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  getEmployeeById,
  seedEmployees,
  updateEmployeeStartStop,
} from "../api/moniter";
import { User as UserType } from "../types";
import { useToast } from "../hooks/useToast";
import { useAccess } from "../contexts/AccessContext";
import PageHeader from "../components/PageHeader";

export function ActivityList() {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [seedingEmployees, setSeedingEmployees] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const { toast } = useToast();
  const { canManage } = useAccess();
  const [activityDate, setActivityDate] = useState("");

  // Work Monitor management surface — Access Management decides, not the role.
  const canManageEmployees = canManage("work-schedule");

  useEffect(() => {
    fetchEmployees();
  }, [selectedDate]);

  useEffect(() => {
    filterEmployees();
  }, [employees, searchQuery, departmentFilter, roleFilter]);

  const fetchEmployees = async () => {
    try {
      const response = await getActivity(selectedDate);
      setEmployees(response.activities || []);
       setActivityDate(response.date);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to load employees",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filterEmployees = () => {
    let filtered = [...employees];

    if (searchQuery) {
      filtered = filtered.filter(
        (emp) =>
          emp.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          emp.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          emp.employeeID?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (departmentFilter !== "all") {
      filtered = filtered.filter((emp) => emp.department === departmentFilter);
    }

    if (roleFilter !== "all") {
      filtered = filtered.filter((emp) => emp.role === roleFilter);
    }

    setFilteredEmployees(filtered);
  };

  const handleEditEmployee = async (employeeData: Partial<UserType>) => {
    if (!selectedEmployee) return;
    try {
      await updateEmployee(selectedEmployee.id, employeeData);
      toast({
        title: "Success",
        description: "Employee updated successfully",
      });
      setSelectedEmployee(null);
      fetchEmployees();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to update employee",
        variant: "destructive",
      });
    }
  };

  const handleDeleteEmployee = async (id: string) => {
    try {
      await deleteEmployee(id);
      toast({
        title: "Success",
        description: "Employee deleted successfully",
      });
      fetchEmployees();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete employee",
        variant: "destructive",
      });
    }
  };

  const handleViewEmployee = async (id: string) => {
    try {
      const response = await getEmployeeById(id);
      setSelectedEmployee(response.employee);
      setShowViewModal(true);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to load employee details",
        variant: "destructive",
      });
    }
  };

  const handleStartStopEmployee = async (id: string, isStart: boolean) => {
    try {
      setIsLoading(true);
      await updateEmployeeStartStop(id, !isStart);
      toast({
        title: "Success",
        description: isStart
          ? "Screen recording stopped"
          : "Screen recording started",
      });
      fetchEmployees();
    } catch (error) {
      toast({
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateToDDMMYYYY = (dateStr) => {
    if (!dateStr) return "-"; // null / undefined handle
    const [year, month, day] = dateStr.split("-"); // "2025-12-15"
    return `${day}-${month}-${year}`; // "15-12-2025"
  };

  const handleSeedEmployees = async () => {
    try {
      setSeedingEmployees(true);
      const response = await seedEmployees();
      toast({
        title: "Success",
        description: `${response.message} Default password: ${response.default_password}`,
      });
      fetchEmployees();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to seed employee data",
        variant: "destructive",
      });
    } finally {
      setSeedingEmployees(false);
    }
  };

  const departments = [...new Set(employees.map((emp) => emp.department))];
  const roles = [...new Set(employees.map((emp) => emp.role))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        eyebrow="III. Work Monitor"
        title={<>Activity <em>List</em></>}
        subtitle={<>See what your <em>team</em> logged today.</>}
        actions={
          canManageEmployees && (
            <Button
              disabled={seedingEmployees}
              onClick={handleSeedEmployees}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {seedingEmployees ? "Seeding..." : "Seed Employees"}
            </Button>
          )
        }
      />

      {/* Filters */}
      <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search employees..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select
              value={departmentFilter}
              onValueChange={setDepartmentFilter}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by department" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept} value={dept}>
                    {dept}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Roles</SelectItem>
                {roles.map((role) => (
                  <SelectItem key={role} value={role}>
                    {role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Employee Table */}
      <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Employees</CardTitle>
          <CardDescription>Today activity overview</CardDescription>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Filter by Date:</Label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-44"
            />
          </div>
        </CardHeader>
        <CardContent className="overflow-auto max-h-[70vh]">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="sticky top-0 z-10 bg-gray-100 px-4 py-2 text-left">Employee</th>
                <th className="sticky top-0 z-10 bg-gray-100 px-4 py-2 text-left">Date</th>
                <th className="sticky top-0 z-10 bg-gray-100 px-4 py-2 text-left">Idle Mintues</th>
                <th className="sticky top-0 z-10 bg-gray-100 px-4 py-2 text-left">Total Working Hour</th>
                <th className="sticky top-0 z-10 bg-gray-100 px-4 py-2 text-left">Tracking Type</th>
                <th className="sticky top-0 z-10 bg-gray-100 px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    No employees found
                  </td>
                </tr>
              )}
              {filteredEmployees.map((employee) => (
                <tr
                  key={employee._id}
                  className="border-b last:border-b-0 hover:bg-gray-50"
                >
                  {/* Employee + avatar + designation */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {/* <Avatar className="h-9 w-9">
                        <AvatarImage
                          src={employee.profilePhoto}
                          alt={employee.name}
                        />
                        <AvatarFallback>
                          {employee.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar> */}
                      <div>
                        {/* <div className="font-medium">{employee.name}</div>
                        <div className="text-xs text-gray-500">
                          {employee.designation} • {employee.department}
                        </div> */}
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          {/* <User className="h-3 w-3" /> */}
                          {employee.employeeName}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Email + LoginId */}
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      {formatDateToDDMMYYYY(activityDate)}
                    </div>
                  </td>

                  {/* Start / End */}
                  <td className="px-4 py-3">
                    <div className="flex flex-col text-sm text-gray-700">
                      {employee.idle_time || "-"}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col text-xs text-gray-700">
                      {employee.active_working_time || "-"}
                    </div>
                  </td>

                  {/* Tracking type */}
                  <td className="px-4 py-3">
                    <span className="text-xs font-medium">
                      {employee.datatype === "screenshot"
                        ? "Screenshot"
                        : "Screen Recording"}
                    </span>
                  </td>

                  {/* Active / Inactive */}
                  {/* <td className="px-4 py-3">
                    <Badge
                      variant={employee.isActive ? "default" : "secondary"}
                      className={employee.isActive ? "bg-green-500" : ""}
                    >
                      {employee.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </td> */}

                  {/* Actions with dropdown */}
                  <td className="px-4 py-3">
                    <div className="flex justify-end">
                      <DropdownMenu>
                        {/* <DropdownMenuTrigger asChild> */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 p-0"
                        >
                          <MoreVertical className="h-4 w-4" />
                          <span className="sr-only">Open row actions</span>
                        </Button>
                        {/* </DropdownMenuTrigger> */}

                        <DropdownMenuContent align="end" className="w-40">
                          {/* View */}
                          <DropdownMenuItem
                            onClick={() => handleViewEmployee(employee._id)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View
                          </DropdownMenuItem>

                          {canManageEmployees && (
                            <>
                              {/* Start / Stop */}
                              <DropdownMenuItem
                                disabled={isLoading}
                                onClick={() =>
                                  handleStartStopEmployee(
                                    employee.id,
                                    employee.start
                                  )
                                }
                              >
                                {employee.start ? (
                                  <>
                                    <StopCircle className="h-4 w-4 mr-2" />
                                    Stop
                                  </>
                                ) : (
                                  <>
                                    <Play className="h-4 w-4 mr-2" />
                                    Start
                                  </>
                                )}
                              </DropdownMenuItem>

                              {/* Edit */}
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedEmployee(employee);
                                  // yaha se edit modal khol sakte ho
                                }}
                              >
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>

                              {/* Delete */}
                              <DropdownMenuItem
                                className="text-red-600 focus:bg-red-50"
                                onClick={() =>
                                  handleDeleteEmployee(employee.id)
                                }
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* View Employee Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-4xl bg-white">
          <DialogHeader>
            <DialogTitle>Employee Details</DialogTitle>
          </DialogHeader>
          {selectedEmployee && <EmployeeDetails employee={selectedEmployee} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmployeeDetails({ employee }: { employee: UserType }) {
  return (
    <Tabs defaultValue="personal" className="w-full">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="personal">Personal Info</TabsTrigger>
        <TabsTrigger value="employment">Employment</TabsTrigger>
        <TabsTrigger value="documents">Documents</TabsTrigger>
      </TabsList>

      <TabsContent value="personal" className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Full Name</Label>
            <div className="p-2 bg-gray-50 rounded">{employee.name}</div>
          </div>
          <div>
            <Label>Email</Label>
            <div className="p-2 bg-gray-50 rounded">{employee.email}</div>
          </div>
          <div>
            <Label>Phone</Label>
            <div className="p-2 bg-gray-50 rounded">
              {employee.phone || "N/A"}
            </div>
          </div>
          <div>
            <Label>Employee ID</Label>
            <div className="p-2 bg-gray-50 rounded">{employee.employeeId}</div>
          </div>
        </div>
        {employee.address && (
          <div>
            <Label>Address</Label>
            <div className="p-2 bg-gray-50 rounded">{employee.address}</div>
          </div>
        )}
        {employee.emergencyContact && (
          <div>
            <Label>Emergency Contact</Label>
            <div className="p-2 bg-gray-50 rounded">
              {employee.emergencyContact.name} (
              {employee.emergencyContact.relationship}) -{" "}
              {employee.emergencyContact.phone}
            </div>
          </div>
        )}
      </TabsContent>

      <TabsContent value="employment" className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Department</Label>
            <div className="p-2 bg-gray-50 rounded">{employee.department}</div>
          </div>
          <div>
            <Label>Designation</Label>
            <div className="p-2 bg-gray-50 rounded">{employee.designation}</div>
          </div>
          <div>
            <Label>Role</Label>
            <div className="p-2 bg-gray-50 rounded">{employee.role}</div>
          </div>
          <div>
            <Label>Date of Joining</Label>
            <div className="p-2 bg-gray-50 rounded">
              {employee.dateOfJoining
                ? new Date(employee.dateOfJoining).toLocaleDateString()
                : "-"}
            </div>
          </div>
          <div>
            <Label>Status</Label>
            <div className="p-2 bg-gray-50 rounded">
              <Badge variant={employee.isActive ? "default" : "secondary"}>
                {employee.isActive ? "Active" : "Inactive"}
              </Badge>
            </div>
          </div>
        </div>
      </TabsContent>

      <TabsContent value="documents">
        <div className="text-center py-8 text-gray-500">
          <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No documents uploaded yet</p>
        </div>
      </TabsContent>
    </Tabs>
  );
}
